// Kontrakt server ↔ klient. Zrcadlí PHP třídy v core/Window (Window::describe, ListWindow::data, Command::toClient…).
// Změna zde = změna i na serveru; kontraktní test porovná ukázkové JSON s těmito typy.

export type WindowKind = 'list' | 'record' | 'custom' | 'console';
export type Mode = 'pc' | 'tablet' | 'mobile';

export interface ParamSpec {
  type: 'int' | 'string' | 'uuid' | 'bool' | 'date' | 'enum';
  optional: boolean;
  values: string[] | null;
}

export interface CommandDef {
  id: string;
  label: string;
  icon: string | null;
  shortcut: string | null;
  enabled: 'always' | 'selection' | 'single' | 'none';
  /** povoleno jen když pole má u všech vybraných řádků jednu z hodnot */
  when: { field: string; values: (string | number | boolean)[] } | null;
  tab: string;
  group: string;
  danger: boolean;
  primary: boolean;
  confirm: string | null;
  kind: 'open' | 'action' | 'link';
  /** rychlá akce na vizitce záznamu */
  quick?: boolean;
  /** popisek při výběru (příkaz pracuje s výběrem, jinak se vším) */
  labelSelected?: string | null;
  /** odkaz (kind link): 'tel:{phone}' – {klíč} z řádku / záznamu */
  href?: string | null;
  /** row = cíl je v řádku (open_app, open_window, open_params) – např. Přehled vede na různé stránky */
  opens: { window: string; params: Record<string, string>; modal: boolean; app: string | null; row?: boolean } | null;
}

export interface EnumOption {
  /** checks: nadpis skupiny (klíč překladu) */
  group?: string;
  value: string | number;
  label: string;
  color?: string;
}

export interface ColumnDef {
  key: string;
  label: string;
  type: 'text' | 'number' | 'datetime' | 'date' | 'bool' | 'enum' | 'image';
  sortable: boolean;
  searchable: boolean;
  width: number | null;
  align: string | null;
  hidden: boolean;
  mono: boolean;
  options: EnumOption[] | null;
  /** enum: barevné kolečko před textem */
  dot?: boolean;
  /** pole řádku s popiskem buňky */
  hint?: string | null;
}

export interface FilterDef {
  key: string;
  label: string;
  type: 'enum' | 'bool' | 'daterange' | 'text';
  options: EnumOption[] | null;
  /** výchozí hodnota filtru při otevření okna a po „Zrušit filtry“ */
  default?: unknown;
}

export type Condition = unknown[];

export interface FieldDef {
  /** pomoc AI u textového pole (false = kód, login, server…) */
  ai?: boolean;
  key: string;
  label: string;
  type: 'text' | 'textarea' | 'email' | 'password' | 'number' | 'bool' | 'date' | 'datetime' | 'select' | 'multi' | 'tags' | 'image' | 'checks' | 'color';
  required: boolean;
  max: number | null;
  min: number | null;
  maxValue: number | null;
  readonly: boolean;
  tab: string;
  help: string | null;
  width: 'full' | 'half' | 'auto';
  default: unknown;
  options: EnumOption[] | null;
  visibleWhen: Condition | null;
  rows: number;
  /** multi: sloupce opakovaných řádků */
  columns?: RowsColumn[] | null;
  /** image: složka aplikace pro nahrání */
  folder?: { app: string; name: string } | null;
  /** tags: návrhy */
  suggest?: string[] | null;
  /** štítky ze společného seznamu jádra (nabídka, barvy) */
  labels?: boolean | null;
  /** šedý text prázdného pole (co platí bez zadání; přeložený serverem) */
  placeholder?: string | null;
  /** číslo: posuvník v rozsahu min–max */
  slider?: boolean | null;
  /** blok formuláře (klíč nadpisu) a nápověda bloku */
  group?: string | null;
  groupHelp?: string | null;
  /** jednotka za polem (klíč textu) */
  unit?: string | null;
  /** odkud je šedá zděděná hodnota („z firmy“) – štítek u řádku nastavení */
  placeholderSource?: string | null;
  /** navazující řádek (klíč textu spojky) */
  follows?: string | null;
  /** stupnice bloku: úseky končí hodnotou pole */
  scale?: { field: string; label: string; tone: string }[] | null;
  scaleMax?: number | null;
}

export interface RowsColumn {
  key: string;
  label: string;
  type: 'text' | 'select' | 'email' | 'phone' | 'url' | 'textarea';
  options?: EnumOption[];
  width?: number;
  /** zdroj napovídání jádra (`lookup.search`), např. 'people' = uživatelé + Kontakty */
  lookup?: string;
  /** po výběru návrhu doplnit sloupce: { sloupec: pole návrhu } */
  fill?: Record<string, string>;
}

/** Výsledek `lookup.search` – návrh z některého zdroje (jádro, aplikace). */
export interface LookupHit {
  label: string;
  detail?: string | null;
  icon?: string | null;
  source: string;
  fields: Record<string, string>;
}

export interface WindowDescriptorBase {
  app: string;
  window: string;
  kind: WindowKind;
  title: string;
  icon: string;
  params: Record<string, unknown>;
  paramSpec: Record<string, ParamSpec>;
  channels: string[];
  live: number | null;
  size: { w: number; h: number };
  minSize?: { w: number; h: number };
  modes: Mode[];
  commands: CommandDef[];
  /** ovládání okna: pás karet, nebo záhlaví s lištou příkazů (koncepce A) */
  chrome?: 'ribbon' | 'bar';
  /** kontextové AI akce okna (kap. 34); prázdné, když uživatel AI nemá */
  aiActions?: AiActionDef[];
}

export interface AiActionDef {
  key: string;
  label: string;
  icon: string;
  sources: string[];
  target: string | null;
  mode: 'replace' | 'append';
}

/** Stav AI pro klienta (boot.ai) */
export interface AiBoot {
  enabled: boolean;
  assistant: boolean;
  online: boolean;
  provider?: string | null;
  budget?: number;
  used?: number;
  actions?: string[];
  directAllowed?: boolean; // správce zákazníka dovoluje přímé zápisy asistenta
  direct?: boolean;        // tento uživatel si je zapnul
}

/** Vrstva = položka levého panelu seznamu (zaškrtávátko s barvou), filtruje sloupec `LayersDef.filter`. */
export interface LayerItem {
  value: string;
  label: string;
  color?: string | null;
  icon?: string | null;
  hint?: string | null;
  /** false = do této vrstvy nejde přesouvat (jen čtení) */
  writable?: boolean;
}
export interface LayersDef {
  filter: string;
  title: string;
  /** řádky jde přetáhnout na vrstvu / „Přesunout do…“ (akce okna `move_layer`) */
  move?: boolean;
  items: LayerItem[];
  /** příkazy okna nabídnuté pod vrstvami ve štítku (Správa adresářů, Nový adresář) */
  commands?: string[];
}
/** Dlaždice: mapování sloupců řádku na kartu. */
export interface CardDef {
  /** obrázek dlaždice bez fotky: iniciály (osoby) nebo ikona (věci – výchozí) */
  media?: 'initials' | 'icon';
  icon?: string | null;
  title: string;
  subtitle?: string | null;
  image?: string | null;
  lines?: string[];
  badge?: string | null;
}

export interface ListDescriptor extends WindowDescriptorBase {
  kind: 'list';
  columns: ColumnDef[];
  filters: FilterDef[];
  rowId: string;
  pageSize: number;
  defaultSort: { col: string; dir: 'asc' | 'desc' } | null;
  searchable: boolean;
  openCommand: string | null;
  layers: LayersDef | null;
  card: CardDef | null;
  /** detail vybraného řádku v pravém podokně (ovládání bar) */
  detail?: { window: string; param: string; app?: string; params?: Record<string, unknown> } | null;
  /** abeceda A–Z u okraje (klíč sloupce jmen) – jen při řazení podle něj vzestupně */
  alphabet?: string | null;
}

export interface RecordDescriptor extends WindowDescriptorBase {
  kind: 'record';
  fields: FieldDef[];
  tabs: Record<string, string>;
  record: Record<string, unknown>;
  /** klíč záznamu pro titulek okna (null = první pole) */
  titleField?: string | null;
  titleNew?: string | null;
  isNew: boolean;
  edit: boolean;
  /** záznam jen pro čtení (všechna pole readonly): bez Upravit / Uložit */
  readonly?: boolean;
  canDelete: boolean;
  /** klíč překladu dotazu před smazáním (výchozí core.cmd.confirm_delete) */
  deleteConfirm?: string;
  /** popisek akce Smazat (výchozí core.cmd.delete) */
  deleteLabel?: string;
  /** vizitka nad záložkami (ovládání bar) */
  card?: { title: string; subtitle?: string; image?: string; media?: 'initials' | 'icon'; icon?: string | null } | null;
  /** rozvržení: stack (popisek nad polem) | rows (řádky nastavení) */
  layout?: 'stack' | 'rows';
  /** vložená okna v záložkách (jen u existujícího záznamu) */
  embeds?: { tab: string; app: string; window: string; params: Record<string, unknown> }[];
}

export interface CustomDescriptor extends WindowDescriptorBase {
  kind: 'custom';
  component: string;
  payload: Record<string, unknown>;
}

/** Uzel stromu konzole správy (ConsoleWindow): skupina (bez okna) nebo stránka = okno aplikace. */
export interface ConsoleNode {
  id: string;
  label: string;
  icon: string | null;
  app: string | null;
  window: string | null;
  params: Record<string, unknown>;
  badge: string | null;
  /** barevné kolečko: ok | warn | bad | info | off */
  dot: string | null;
  /** šedý popisek (neaktivní, zrušený) */
  muted: boolean;
  /** rozbalený smí být jen jeden potomek (zákazníci) */
  exclusive: boolean;
  /** potomci se načtou až po rozbalení (akce children) */
  lazy: boolean;
  children: ConsoleNode[];
}

export interface ConsoleSearchResult {
  group: string;
  id: string;
  label: string;
  sub: string;
  icon: string | null;
}

export interface ConsoleDescriptor extends WindowDescriptorBase {
  kind: 'console';
  tree: ConsoleNode[];
  /** uzel z parametru node (i dynamický – cestu k němu rozbalí konzole) */
  start: string | null;
  /** první stránka stromu */
  first: string | null;
}

export type WindowDescriptor = ListDescriptor | RecordDescriptor | CustomDescriptor | ConsoleDescriptor;

export interface ListDataRequest {
  page: number;
  pageSize: number;
  sort?: { col: string; dir: 'asc' | 'desc' };
  search?: string;
  filters?: Record<string, unknown>;
  /** zaškrtnuté vrstvy; neposílá se, když jsou všechny */
  layers?: string[];
}

export interface ListDataResponse {
  rows: Record<string, unknown>[];
  total: number;
  page: number;
  pageSize: number;
}

export interface CommandResult {
  toast?: { text: string; kind: 'info' | 'ok' | 'warn' | 'error'; params?: Record<string, unknown> };
  refresh?: boolean;
  close?: boolean;
  result?: unknown;
  open?: { app: string | null; window: string; params: Record<string, unknown>; modal: boolean };
  download?: string;
  data?: unknown;
}

export interface ApiErrorBody {
  error: {
    code: string;
    message: string;
    fields: Record<string, string> | null;
    params: Record<string, unknown> | null;
    id: string;
    debug?: string;
  };
}

export interface EventItem {
  id: number;
  ch: string;
  rid: string | null;
  t: string;
  u: string | null;
}

export interface EventsResponse {
  cursor: number;
  reset: boolean;
  events: EventItem[];
  session: { locked: boolean };
  now: string;
}

export interface AppWindowInfo {
  id: string;
  title: string;
  icon: string;
  inStart: boolean;
  /** oddíl Startu: Aplikace / Správa */
  startGroup?: 'apps' | 'admin';
  kind: WindowKind;
}

export interface AppInfo {
  id: string;
  name: string;
  icon: string;
  version: string;
  system: boolean;
  main: string | null;
  windows: AppWindowInfo[];
  tray: { icon: string; window?: string; badgeChannel?: string; badge?: { window: string; action: string } } | null;
  fileTypes: Record<string, string>;
}

export interface UserInfo {
  id: string;
  login: string;
  name: string;
  email: string | null;
  isAdmin: boolean;
  lang: string;
  permissions?: string[];
}

export interface SessionBoot {
  user: UserInfo & { permissions: string[] };
  session: { id: string; kind: string; parentId: string | null; secret: string; lockMinutes: number };
  customer: { code: string; name: string };
  system: { name: string; version: string; vendor: { name: string; url: string } };
  apps: AppInfo[];
  prefs: Record<string, unknown>;
  branding: Record<string, unknown>;
  lang: string;
  texts: Record<string, string | Record<string, string>>;
  desktopFolderId: string;
  eventsCursor: number;
  now: string;
  ai?: AiBoot;
}

export interface PageBoot {
  prefix: string;
  customer: { code: string; name: string };
  system: { name: string; version: string; vendor: { name: string; url: string } };
  branding: Record<string, unknown>;
  langs: string[];
  defaultLang: string;
  assetHash: string;
}

export interface StorageItem {
  id: string;
  parentId: string | null;
  space: 'user' | 'shared' | 'app';
  kind: 'folder' | 'file';
  name: string;
  mime: string | null;
  size: number;
  createdAt: string;
  updatedAt: string | null;
  deletedAt: string | null;
  isShortcut: boolean;
  /** štítky (názvy ze společného seznamu jádra) */
  tags?: string[];
  url?: string;
  /** u složek: má podsložky (strom ukáže šipku) */
  hasFolders?: boolean;
  version?: number;
  /** úroveň přístupu přihlášeného: owner | manage | write | read | null */
  access?: 'owner' | 'manage' | 'write' | 'read' | null;
  /** položka je s někým sdílená (odznak) */
  shared?: boolean;
  /** v pohledu „Sdílené se mnou“: kdo nasdílel */
  sharedByName?: string | null;
}

export const SHARED_ROOT = '__shared__';
export function canWrite(access: StorageItem['access'] | undefined): boolean {
  return access === 'owner' || access === 'manage' || access === 'write';
}
export function canManage(access: StorageItem['access'] | undefined): boolean {
  return access === 'owner' || access === 'manage';
}

export interface Shortcut {
  app: string;
  window: string;
  params: Record<string, unknown>;
  icon: string | null;
}

export const SHORTCUT_MIME = 'application/x-iwan-shortcut';
